import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Receipt } from '../types';
import * as receiptsApi from '../api/receiptsApi';
import type { CreateReceiptPayload } from '../api/receiptsApi';

/**
 * État de la feature « receipts » : tickets/factures persistés via l'API.
 */
export interface ReceiptsState {
  items: Receipt[];
  /** État du chargement initial depuis l'API. */
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ReceiptsState = {
  items: [],
  status: 'idle',
  error: null,
};

/** Charge les receipts depuis l'API. */
export const fetchReceipts = createAsyncThunk<Receipt[], void, { rejectValue: string }>(
  'receipts/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await receiptsApi.fetchReceipts();
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Impossible de charger les tickets.',
      );
    }
  },
);

/** Persiste un ticket validé via l'API, puis l'ajoute au store. */
export const createReceipt = createAsyncThunk<
  Receipt,
  CreateReceiptPayload,
  { rejectValue: string }
>('receipts/create', async (payload, { rejectWithValue }) => {
  try {
    return await receiptsApi.createReceipt(payload);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Impossible d'enregistrer le ticket.",
    );
  }
});

/** Supprime un ticket via l'API, puis le retire du store. */
export const deleteReceipt = createAsyncThunk<string, string, { rejectValue: string }>(
  'receipts/delete',
  async (id, { rejectWithValue }) => {
    try {
      await receiptsApi.deleteReceipt(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Impossible de supprimer le ticket.',
      );
    }
  },
);

/** Valide un ticket (ou le remet en revue) via l'API. */
export const setReceiptStatus = createAsyncThunk<
  Receipt,
  { id: string; status: 'validated' | 'needs_review' },
  { rejectValue: string }
>('receipts/setStatus', async ({ id, status }, { rejectWithValue }) => {
  try {
    return await receiptsApi.setReceiptStatus(id, status);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Impossible de modifier le statut.',
    );
  }
});

export const receiptsSlice = createSlice({
  name: 'receipts',
  initialState,
  reducers: {
    receiptRemoved(state, action: PayloadAction<string>) {
      state.items = state.items.filter((receipt) => receipt.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Chargement
      .addCase(fetchReceipts.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchReceipts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchReceipts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Erreur inconnue';
      })
      // Création (le serveur renvoie le receipt complet : id, statut, dates)
      .addCase(createReceipt.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      // Suppression
      .addCase(deleteReceipt.fulfilled, (state, action) => {
        state.items = state.items.filter((receipt) => receipt.id !== action.payload);
      })
      // Changement de statut (validation / remise en revue)
      .addCase(setReceiptStatus.fulfilled, (state, action) => {
        const index = state.items.findIndex((receipt) => receipt.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
      });
  },
  selectors: {
    selectReceipts: (state) => state.items,
    selectReceiptsStatus: (state) => state.status,
    selectReceiptsError: (state) => state.error,
    selectReceiptById: (state, id: string) => state.items.find((receipt) => receipt.id === id),
    selectReceiptsCount: (state) => state.items.length,
  },
});

export const { receiptRemoved } = receiptsSlice.actions;
export const {
  selectReceipts,
  selectReceiptsStatus,
  selectReceiptsError,
  selectReceiptById,
  selectReceiptsCount,
} = receiptsSlice.selectors;
